import React, { useRef, useMemo, useContext } from 'react';
import { ReactReduxContext, Provider } from 'react-redux';
import { subspace, parentSpace } from 'redux-subspace';
import hoistNonReactStatics from 'hoist-non-react-statics';
import PropTypes from 'prop-types';

/**
 * Copyright 2017, IOOF Holdings Limited.
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */
var isObject = function isObject(value) {
  var type = typeof value;
  return type == "object" && value != null && !Array.isArray(value);
};

/**
 * Copyright 2017, IOOF Holdings Limited.
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */
var isFunction = function isFunction(value) {
  return typeof value === "function";
};

/**
 * Copyright 2017, IOOF Holdings Limited.
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */
var useSubspace = function useSubspace(mapState, namespace, options) {
  if (options === undefined && isObject(namespace)) {
    return useSubspace(mapState, undefined, namespace);
  }

  var _ref = options || {},
      _ref$context = _ref.context,
      context = _ref$context === void 0 ? ReactReduxContext : _ref$context;

  var mapStateFunc = useRef(mapState);
  mapStateFunc.current = mapState;
  var subspacer = isFunction(mapState) ? useMemo(function () {
    return subspace(function () {
      return mapStateFunc.current.apply(mapStateFunc, arguments);
    }, namespace);
  }, [mapStateFunc, namespace]) : useMemo(function () {
    return subspace(mapState, namespace);
  }, [mapState, namespace]);

  var _useContext = useContext(context),
      store = _useContext.store;

  return useMemo(function () {
    return subspacer(store);
  }, [subspacer, store]);
};

/**
 * Copyright 2017, IOOF Holdings Limited.
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

var useParentSpace = function useParentSpace(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      _ref$context = _ref.context,
      context = _ref$context === void 0 ? ReactReduxContext : _ref$context;

  var _useContext = useContext(context),
      store = _useContext.store;

  return useMemo(function () {
    return parentSpace(store);
  }, [store]);
};

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

/**
 * Copyright 2017, IOOF Holdings Limited.
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

var SubspaceProvider = function SubspaceProvider(_ref) {
  var mapState = _ref.mapState,
      namespace = _ref.namespace,
      _ref$context = _ref.context,
      context = _ref$context === void 0 ? ReactReduxContext : _ref$context,
      children = _ref.children;
  var _context$parent = context.parent,
      ParentContext = _context$parent === void 0 ? context.Consumer ? context : ReactReduxContext : _context$parent,
      _context$child = context.child,
      ChildContext = _context$child === void 0 ? context.Consumer ? context : ReactReduxContext : _context$child;
  var subspacedStore = useSubspace(mapState, namespace, {
    context: ParentContext
  });
  return /*#__PURE__*/React.createElement(Provider, {
    store: subspacedStore,
    context: ChildContext
  }, children);
};

SubspaceProvider.propTypes = {
  children: PropTypes.element.isRequired,
  mapState: /*#__PURE__*/PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  namespace: PropTypes.string,
  context: /*#__PURE__*/PropTypes.oneOfType([/*#__PURE__*/PropTypes.shape({
    parent: PropTypes.object,
    child: PropTypes.object
  }), PropTypes.object])
};

var createDisplayNameForWrappedComponent = function createDisplayNameForWrappedComponent(WrappedComponent, wrapperName) {
  if (!WrappedComponent || !wrapperName) {
    return undefined;
  }

  var componentName = "Component";

  if (WrappedComponent.displayName) {
    componentName = WrappedComponent.displayName;
  } else if (WrappedComponent.name) {
    componentName = WrappedComponent.name;
  } else if (typeof WrappedComponent === "string") {
    componentName = WrappedComponent;
  }

  return wrapperName + "(" + componentName + ")";
};

var subspaced = function subspaced(mapState, namespace, subspaceOptions) {
  if (subspaceOptions === undefined && isObject(namespace)) {
    return subspaced(mapState, undefined, namespace);
  }

  return function (WrappedComponent) {
    var SubspacedComponent = /*#__PURE__*/React.forwardRef(function (props, ref) {
      return /*#__PURE__*/React.createElement(SubspaceProvider, _extends({}, subspaceOptions, {
        mapState: mapState,
        namespace: namespace
      }), /*#__PURE__*/React.createElement(WrappedComponent, _extends({}, props, {
        ref: ref
      })));
    });
    hoistNonReactStatics(SubspacedComponent, WrappedComponent);
    SubspacedComponent.displayName = createDisplayNameForWrappedComponent(WrappedComponent, "Subspaced");
    return SubspacedComponent;
  };
};

/**
 * Copyright 2017, IOOF Holdings Limited.
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

var ParentSpaceProvider = function ParentSpaceProvider(_ref) {
  var _ref$context = _ref.context,
      context = _ref$context === void 0 ? ReactReduxContext : _ref$context,
      children = _ref.children;
  var _context$parent = context.parent,
      ParentContext = _context$parent === void 0 ? context.Consumer ? context : ReactReduxContext : _context$parent,
      _context$child = context.child,
      ChildContext = _context$child === void 0 ? context.Consumer ? context : ReactReduxContext : _context$child;
  var parentStore = useParentSpace({
    context: ParentContext
  });
  return /*#__PURE__*/React.createElement(Provider, {
    store: parentStore,
    context: ChildContext
  }, children);
};

ParentSpaceProvider.propTypes = {
  children: PropTypes.element.isRequired,
  context: /*#__PURE__*/PropTypes.oneOfType([/*#__PURE__*/PropTypes.shape({
    parent: PropTypes.object,
    child: PropTypes.object
  }), PropTypes.object])
};

var parentSpaced = function parentSpaced(parentSpaceOptions) {
  return function (WrappedComponent) {
    var ParentSpacedComponent = /*#__PURE__*/React.forwardRef(function (props, ref) {
      return /*#__PURE__*/React.createElement(ParentSpaceProvider, parentSpaceOptions, /*#__PURE__*/React.createElement(WrappedComponent, _extends({}, props, {
        ref: ref
      })));
    });
    hoistNonReactStatics(ParentSpacedComponent, WrappedComponent);
    ParentSpacedComponent.displayName = createDisplayNameForWrappedComponent(WrappedComponent, "ParentSpaced");
    return ParentSpacedComponent;
  };
};

export { ParentSpaceProvider, SubspaceProvider, parentSpaced, subspaced, useParentSpace, useSubspace };
